import { isValidElement } from 'react';
import { DotsThreeVertical } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * Standard row-actions kebab menu used across all document/transaction tables.
 *
 * actions: Array<Action | 'separator'>
 *   Action = {
 *     id?: string,           // optional, used for data-testid
 *     label: string,
 *     icon?: ReactNode | Component,
 *     onClick: () => void,
 *     disabled?: boolean,
 *     hidden?: boolean,
 *     destructive?: boolean,
 *   }
 *
 * Skipped actions (hidden=true OR onClick missing) are not rendered.
 * Pure separators are collapsed if adjacent.
 *
 * Usage:
 *   <RowActionsMenu testid={`row-${row.id}`} actions={[
 *     { id: 'view',   label: 'View',     icon: Eye,    onClick: () => view(row) },
 *     { id: 'edit',   label: 'Edit',     icon: Pencil, onClick: () => edit(row) },
 *     'separator',
 *     { id: 'delete', label: 'Delete',   icon: Trash,  onClick: () => del(row), destructive: true },
 *   ]} />
 */
export default function RowActionsMenu({ actions = [], testid, align = 'end', size = 'sm', className }) {
  // Filter hidden/empty
  const cleaned = actions.filter((a) => {
    if (a === 'separator') return true;
    if (!a) return false;
    if (a.hidden) return false;
    if (typeof a.onClick !== 'function') return false;
    return true;
  });

  // Collapse leading/trailing/duplicate separators
  const final = [];
  for (const a of cleaned) {
    if (a === 'separator') {
      if (final.length === 0) continue;
      if (final[final.length - 1] === 'separator') continue;
      final.push('separator');
    } else {
      final.push(a);
    }
  }
  while (final.length && final[final.length - 1] === 'separator') final.pop();

  if (final.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8 p-0 hover:bg-muted', className)}
          data-testid={testid ? `${testid}-menu-trigger` : 'row-actions-menu-trigger'}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsThreeVertical size={18} weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="w-48"
        onClick={(e) => e.stopPropagation()}
      >
        {final.map((a, idx) => {
          if (a === 'separator') return <DropdownMenuSeparator key={`sep-${idx}`} />;
          const Icon = a.icon;
          const itemTestid = a.id && testid ? `${testid}-action-${a.id}` : (a.id ? `action-${a.id}` : undefined);
          return (
            <DropdownMenuItem
              key={a.id || a.label || idx}
              disabled={a.disabled}
              onClick={(e) => {
                e.stopPropagation();
                a.onClick();
              }}
              className={cn(a.destructive && 'text-destructive focus:text-destructive')}
              data-testid={itemTestid}
            >
              {Icon && (isValidElement(Icon)
                ? <span className="mr-2">{Icon}</span>
                : <Icon size={16} className="mr-2" />)}
              {a.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
